// ═════════════════════════════════════════════════════════════════════════════
//  WHEN A RECONCILED CONTRACT LINE MAY BE DELETED
// ═════════════════════════════════════════════════════════════════════════════
// A contract line with source 'change_order' or 'extra_work_order' is not typed
// by hand — jvReconcileChangeOrders mints it when the source record becomes
// approved. So its ✕ is disabled: deleting it would only have it recreated at
// 0% on the next render, which reads as the app fighting the admin.
//
// The escape hatch the UI advertises — "un-approve the source record" — does
// not work, and that is the bug this file exists for. A line's EXISTENCE is
// insert-only (jvReconcileChangeOrders: "if a change order is un-approved
// later the line stays and the admin decides" — only its AMOUNT follows the
// source, see reconcilePlan below), so un-approving leaves the line behind,
// still counted as contract value on Contract, Job Costs and the P&L — and the
// ✕ was still disabled, so the admin could not decide anything.
//
// Found in production 2026-08-14: three lines worth $12,950.17 of contract
// value, $5,576.84 of it already booked as earned, all pointing at change
// orders whose co_status had gone back to 'draft' or 'sent'.
//
// The rule below replaces "is it reconciled?" with "is its source STILL the
// thing that justified it?". A line stays locked exactly as long as the record
// behind it still qualifies; the moment it does not — un-approved, or deleted
// outright — the line becomes the admin's to remove.

// The states in which a source record justifies a contract line. ONE list per
// source, read by everything that asks the question: this file's lock, the
// reconciler's queries (jvReconcileChangeOrders filters with these arrays, it
// no longer carries its own literals) and the database trigger in
// 20261001110000_contract_line_source_sync.sql, which must name the same
// states.
//
// 'billed' counts for an EWO exactly as it does for a change order: billing
// comes AFTER approval ("approved and billed are both finished states"). With
// only 'approved' here, the day an owner marked an EWO as Billed its line
// turned into "Source not approved" with a live ✕ — an invitation to delete
// revenue that had just been invoiced — and an EWO taken straight from Sent
// to Billed never got a line at all.
export const CO_LIVE_STATES  = ['approved', 'billed'];
export const EWO_LIVE_STATES = ['approved', 'billed'];

// Builds { source_id -> true } for every source record that STILL qualifies.
// Anything absent from this map — un-approved, or gone from the table — no
// longer justifies its line.
export function liveSourceIds(siRows, ewoRows) {
  const live = new Set();
  (siRows || []).forEach(r => {
    if (r && r.id && CO_LIVE_STATES.includes(r.co_status)) live.add(r.id);
  });
  (ewoRows || []).forEach(r => {
    if (r && r.id && EWO_LIVE_STATES.includes(r.status)) live.add(r.id);
  });
  return live;
}

// May the admin delete this line?
//
//   'base'                     — always; it was typed by hand.
//   reconciled + source live   — no; deleting it would recreate it at 0%.
//   reconciled + source stale  — YES. This is the case that was stuck.
//   reconciled + no source_id  — YES; nothing can recreate it.
export function canDeleteLine(line, liveIds) {
  if (!line) return false;
  if (line.source === 'base' || !line.source) return true;
  if (!line.source_id) return true;
  return !(liveIds && liveIds.has(line.source_id));
}

// ── What the reconciler has to write ────────────────────────────────────────
// Pure diff between the live source records and the lines already on the job:
//
//   inserts  a live source with money on it and no line yet.
//   updates  a live source whose line carries a different amount. The line is
//            a COPY of the source's amount, and a copy nobody refreshes is a
//            second fact: a change order re-priced after approval kept the
//            old money on the contract forever, and the amount of a CO/EWO
//            line is not editable by hand ("it belongs to its source").
//
// Existence stays insert-only: a source that stopped qualifying is simply not
// in these lists, so its line is left where it is, with its "Source not
// approved" chip, for the admin to decide. description, percent_complete,
// continues_line_id and the plan are never touched — the admin owns those.
//
// A zero/blank amount blocks only the INSERT (no line for nothing). A line
// that already exists follows its source down to 0 rather than keeping money
// the source no longer has.
//
// A CREDIT IS BORN AT 100% (owner's decision, 2026-10-02). A deductive change
// order lowers the contract the moment it is approved; minted at 0% it left
// Earned where it was until somebody typed "100" on a negative line, so the
// job read "103% of contract" in between. A negative line is therefore
// created fully counted, a positive one at 0% as always. Only at creation:
// percent_complete is the admin's from then on — he sets it back to 0% when
// the credit removes units a quantity line will simply never install (the
// missing units already lower Earned; the row says so). A line that turns
// negative AFTER it exists keeps the percent it had. The trigger and the
// backfill in 20261001110000 use the same rule (pinned by the unit test).
//
// Amounts go out UNROUNDED and the comparison allows half a cent: the column
// is numeric(12,2) and Postgres does the rounding, the same rounding the
// trigger does. Rounding here as well would give two rounders that can
// disagree by a cent on an x.xx5 float and rewrite the line on every paint
// (extra_work_orders.total_amount holds values like 1752.2788799999998).
const _amt = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export function reconcilePlan(siRows, ewoRows, existingLines) {
  const have = new Map();
  (existingLines || []).forEach(l => {
    if (l && l.source && l.source !== 'base' && l.source_id) have.set(l.source + '|' + l.source_id, l);
  });
  const inserts = [], updates = [];
  const visit = (source, sourceId, rawAmount, description, sort_order) => {
    const amount = _amt(rawAmount);
    const line = have.get(source + '|' + sourceId);
    if (!line) {
      if (amount) {
        inserts.push({ description, earn_method: 'percent', contract_amount: amount,
                       percent_complete: amount < 0 ? 100 : 0,
                       source, source_id: sourceId, sort_order });
      }
    } else if (Math.abs(amount - _amt(line.contract_amount)) > 0.005 + 1e-9) {
      updates.push({ id: line.id, contract_amount: amount });
    }
  };
  (siRows || []).forEach(si => {
    if (!si || !si.id || !CO_LIVE_STATES.includes(si.co_status)) return;
    // co_number already carries the full human label in production data
    // ("Change order 07") — prepending our own "Change order" doubles it. Use
    // it as-is when it's there; build a label from si_number only when it is
    // missing or blank.
    const co = (si.co_number || '').trim();
    visit('change_order', si.id, si.amount,
          co || `Change order ${(si.si_number || '').trim()}`.trim(), 900);
  });
  (ewoRows || []).forEach(ewo => {
    if (!ewo || !ewo.id || !EWO_LIVE_STATES.includes(ewo.status)) return;
    visit('extra_work_order', ewo.id, ewo.total_amount,
          `Extra work ${ewo.ewo_number || ''}`.trim(), 910);
  });
  return { inserts, updates };
}

// Why a line is showing as stale, for the chip beside it. Null when the line is
// in good standing — the caller prints nothing.
export function staleReason(line, liveIds) {
  if (!line || line.source === 'base' || !line.source) return null;
  if (line.source_id && liveIds && liveIds.has(line.source_id)) return null;
  return line.source === 'change_order' ? 'co' : 'ewo';
}
